import React from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { getAgencySettingsOptions, editAgencyComprehensiveAssessmentSettingsOptions } from '@generated';
import Yup from '~/util/yup';
import { CheckboxFormField } from '~/shared/forms/controls/fields/CheckboxFormField';
import { TextAreaFormField } from '~/shared/forms/controls/fields/text-fields/TextAreaFormField';
import { FormLabel } from '~/shared/forms/controls/base-controls/FormLabel';
import type { SettingsSectionItem } from '~/libraries/settings/SettingsSection';
import { Globe } from 'lucide-react';
import { LibraryForm } from '../shared/LibraryForm';

export const ComprehensiveAssessmentAgencySettingsSection = {
	settingsView: 'assessment',
	title: 'Comprehensive Assessment Settings',
	description: 'Manage settings related to comprehensive assessments',
	TitleIcon: Globe
} satisfies SettingsSectionItem;

const validationSchema = Yup.object().shape({
	physicalTherapistCanCompleteComprehensiveAssessment: Yup.boolean().label('Physical Therapist'),
	speechTherapistCanCompleteComprehensiveAssessment: Yup.boolean().label('Speech Therapist'),
	occupationalTherapistCanCompleteComprehensiveAssessment: Yup.boolean().label('Occupational Therapist'),
	canScheduleInitialAndComprehensiveSeperately: Yup.boolean().label('Can Schedule Initial And Comprehensive Separately'),
	certificationStatement: Yup.string().label('Certification Statement').required().nullable()
});

export function ComprehensiveAssessmentAgencySettingsForm() {
	const { data: settings } = useSuspenseQuery(getAgencySettingsOptions());

	return (
		<LibraryForm
			initialValues={{
				physicalTherapistCanCompleteComprehensiveAssessment: settings.physicalTherapistCanCompleteComprehensiveAssessment,
				speechTherapistCanCompleteComprehensiveAssessment: settings.speechTherapistCanCompleteComprehensiveAssessment,
				occupationalTherapistCanCompleteComprehensiveAssessment: settings.occupationalTherapistCanCompleteComprehensiveAssessment,
				canScheduleInitialAndComprehensiveSeperately: settings.canScheduleInitialAndComprehensiveSeperately,
				certificationStatement: settings.certificationStatement
			}}
			saveMutation={{
				...editAgencyComprehensiveAssessmentSettingsOptions,
				invalidateQueryKeys: [getAgencySettingsOptions().queryKey]
			}}
			validationSchema={validationSchema}
			{...ComprehensiveAssessmentAgencySettingsSection}
		>
			<div className="flex flex-col gap-4">
				<FormLabel label="Disciplines Allowed to Complete Comprehensive Assessments" />
				<CheckboxFormField name="physicalTherapistCanCompleteComprehensiveAssessment" hideLabel />
				<CheckboxFormField name="speechTherapistCanCompleteComprehensiveAssessment" hideLabel />
				<CheckboxFormField name="occupationalTherapistCanCompleteComprehensiveAssessment" hideLabel />
			</div>
			<CheckboxFormField name="canScheduleInitialAndComprehensiveSeperately" hideLabel />
			<TextAreaFormField name="certificationStatement" />
		</LibraryForm>
	);
}
